import type { ThemeOptions } from "@mui/material";
import type { LinkProps } from "@mui/material/Link";
import * as React from "react";
import { defaultTheme } from "react-admin";
import {
    Link as RouterLink,
    type LinkProps as RouterLinkProps,
} from "react-router-dom";
import { distinct } from "./array";
import type { Website } from "./models";
import { rgbToHex } from "./util";

const basic = {
    mode: "light",
    primaryColor: "#ffffff",
    secondaryColor: "#ffffff",
    primaryFont: "Lato",
    secondaryFont: "Poppins",
};

const LinkBehavior = React.forwardRef<
    HTMLAnchorElement,
    Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }
>((props, ref) => {
    const { href, ...other } = props;
    // Map href (Material UI) -> to (react-router)
    return <RouterLink ref={ref} to={href} {...other} viewTransition />;
});

export const themeCreate = ({
    mode,
    primaryColor,
    secondaryColor,
    primaryFont,
    secondaryFont,
}: Website): ThemeOptions =>
({
    cssVariables: true,
    palette: {
        mode: mode ?? basic.mode,
        primary: { main: primaryColor ? rgbToHex(primaryColor) : basic.primaryColor  },
        secondary: { main: secondaryColor ? rgbToHex(secondaryColor) : basic.secondaryColor  },
    },
    typography: {
        fontFamily: distinct(
            [
                `"${primaryFont ?? basic.primaryFont}"`,
                `"${secondaryFont ?? basic.secondaryFont}"`,
                `"Roboto"`,
            ],
            x => x,
        ).join(","),
        h1: { fontFamily: secondaryFont },
        h2: { fontFamily: secondaryFont },
        h3: { fontFamily: secondaryFont },
        h4: { fontFamily: secondaryFont },
        h5: { fontFamily: secondaryFont },
        h6: { fontFamily: secondaryFont },
    },
    components: {
        ...defaultTheme.components,
        MuiTextField: {
            defaultProps: {
                fullWidth: true,
                size: "small",
                variant: "outlined",
            },
        },
        MuiFormControl: {
            defaultProps: {
                size: "small",
                variant: "outlined",
            },
        },
        MuiTabs: {
            defaultProps: {
                textColor: "secondary"
            },
        },
        MuiLink: {
            defaultProps: {
                color: "primary",
                component: LinkBehavior,
            } as LinkProps,
        },
        MuiButtonBase: {
            defaultProps: {
                LinkComponent: LinkBehavior,
            },
        },
        MuiButton: {
            defaultProps: {
                color: "secondary",
            },
        },
        MuiSwitch: {
            defaultProps: {
                color: "secondary",
            },
        },
        MuiIconButton: {
            defaultProps: {
                color: "secondary",
            },
        },
        MuiSelect: {
            styleOverrides: {
                icon: {
                    color: secondaryColor ? rgbToHex(secondaryColor) : basic.secondaryColor
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    background: mode === "light" ? "rgba(255,255,255,0.7)" : "rgba(18,18,18,0.7)",
                    backdropFilter: "blur(24px) saturate(1.35)"
                }
            }
        }
    },
});

export const fontFace = (font: string): string => `
    @font-face {
        font-family: "${font}";
        font-style: normal;
        font-weight: 400;
        font-display: swap;
        ${fonts.includes(font)
        ? `src: url("${location.origin}/fonts/${font}.woff2") format('woff2');`
        : ""
    }
    }
`;

const fonts = ["Lato", "Montserrat", "Poppins", "Roboto", "Ubuntu"];
